import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EMPLOYEE_STATUSES, fmtDate, type EmployeeStatus } from '@em/shared';
import { useEmployees, useTeams } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { titleCase } from '../../components/format';
import { Badge, Button, Card, EmptyState, Field, Input, PageHeader, QueryState, Select } from '../../components/ui';
import { EmployeeForm } from './EmployeeForm';

const TYPE = { full_time: 'Full time', part_time: 'Part time', casual: 'Casual' } as const;

export function EmployeesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<EmployeeStatus | 'all'>('active');
  const [teamId, setTeamId] = useState('');
  const [creating, setCreating] = useState(false);
  const q = useEmployees({ status, teamId: teamId || undefined, q: search.trim() || undefined });
  const teams = useTeams();

  return (
    <>
      <PageHeader
        title="Employees"
        actions={
          can('payroll_admin') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              Add employee
            </Button>
          )
        }
      />
      <div className="filters">
        <Field label="Search" className="wide">
          <Input type="search" placeholder="Name or employee number" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as EmployeeStatus | 'all')}>
            {EMPLOYEE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
            <option value="all">All</option>
          </Select>
        </Field>
        <Field label="Team">
          <Select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">All teams</option>
            {teams.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Card tight>
        <QueryState q={q} loadingLabel="Loading employees">
          {(list) =>
            list.length === 0 ? (
              <EmptyState title="No employees match" />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Number</th>
                      <th>Type</th>
                      <th>Team</th>
                      <th>Started</th>
                      <th>MYOB</th>
                      <th>Kiosk</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((e) => (
                      <tr key={e.id}>
                        <td className="strong">
                          <Link to={`/employees/${e.id}`}>{e.displayName}</Link>
                          {e.preferredName && <span className="muted small"> ({e.firstName} {e.lastName})</span>}
                        </td>
                        <td>{e.employeeNumber}</td>
                        <td>{TYPE[e.employmentType]}</td>
                        <td>{e.teamName ?? <span className="muted">–</span>}</td>
                        <td className="nowrap">{fmtDate(e.startDate)}</td>
                        <td>{e.myobUid ? <Badge tone="green">Matched</Badge> : e.myobCardId ? <Badge>Card {e.myobCardId}</Badge> : <Badge tone="amber">Not set</Badge>}</td>
                        <td>{e.kioskEnabled ? (e.hasPin ? 'PIN set' : <span className="amber">No PIN</span>) : <span className="muted">Off</span>}</td>
                        <td>
                          <Badge tone={e.status === 'active' ? 'green' : e.status === 'suspended' ? 'amber' : 'grey'}>{titleCase(e.status)}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </QueryState>
      </Card>
      {creating && <EmployeeForm onClose={() => setCreating(false)} onSaved={(e) => navigate(`/employees/${e.id}`)} />}
    </>
  );
}
